import type { ListModule } from '../config/constants';

/**
 * Metade do orçamento documentado de ~120 writes/min do chrome.storage.sync. O intervalo deixa
 * margem para início/fim da captura e outras escritas, sem deixar o badge parado por muito tempo.
 */
export const CAPTURE_COUNT_SYNC_INTERVAL_MS = 1000;

export const SYNC_COUNT_KEY = {
  emails: 'countEmails',
  phones: 'countPhones',
  places: 'countPlaces',
  hunter: 'countHunter',
} as const satisfies Record<ListModule, string>;

type TimeoutHandle = ReturnType<typeof setTimeout>;

export interface CaptureCountSyncClock {
  now: () => number;
  setTimeout: (callback: () => void, delayMs: number) => TimeoutHandle;
  clearTimeout: (handle: TimeoutHandle) => void;
}

export interface CaptureCountSync {
  /** Recebe todo ACK positivo; dentro da janela conserva somente a contagem real mais recente. */
  publish: (module: ListModule, count: number, runId?: string) => void;
  /** Fura o throttle, cancela pendências e publica o total final junto com `play:false`. */
  finish: (module: ListModule, count: number, runId?: string) => void;
  reset: () => void;
}

interface CaptureCountSyncOptions {
  clock?: CaptureCountSyncClock;
  intervalMs?: number;
}

const DEFAULT_CLOCK: CaptureCountSyncClock = {
  now: () => Date.now(),
  setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimeout: (handle) => clearTimeout(handle),
};

export function createCaptureCountSync(
  setSync: (data: Record<string, unknown>) => void,
  options: CaptureCountSyncOptions = {},
): CaptureCountSync {
  const clock = options.clock ?? DEFAULT_CLOCK;
  const intervalMs = Math.max(1, Math.trunc(options.intervalMs ?? CAPTURE_COUNT_SYNC_INTERVAL_MS));
  let lastWriteAt: number | null = null;
  let timer: TimeoutHandle | null = null;
  let pending: { module: ListModule; count: number; runId?: string } | null = null;

  const cancelTimer = () => {
    if (timer === null) return;
    clock.clearTimeout(timer);
    timer = null;
  };

  const flushPending = () => {
    if (!pending) return;
    const current = pending;
    pending = null;
    lastWriteAt = clock.now();
    setSync({
      [SYNC_COUNT_KEY[current.module]]: current.count,
      ...(current.runId ? { captureRunId: current.runId } : {}),
    });
  };

  const reset = () => {
    cancelTimer();
    pending = null;
    lastWriteAt = null;
  };

  return {
    publish(module, count, runId) {
      pending = { module, count, ...(runId ? { runId } : {}) };
      const now = clock.now();
      const elapsed = lastWriteAt === null ? intervalMs : now - lastWriteAt;

      if (elapsed >= intervalMs) {
        cancelTimer();
        flushPending();
        return;
      }

      if (timer !== null) return;
      timer = clock.setTimeout(() => {
        timer = null;
        flushPending();
      }, intervalMs - elapsed);
    },

    finish(module, count, runId) {
      cancelTimer();
      pending = null;
      lastWriteAt = clock.now();
      setSync({
        play: false,
        // O runId final permanece nesta escrita para a extensão correlacionar a celebração,
        // mas uma captura concluída não pode deixar um listId restaurável para a próxima janela.
        captureListId: '',
        [SYNC_COUNT_KEY[module]]: count,
        ...(runId ? { captureRunId: runId } : {}),
      });
    },

    reset,
  };
}
